import { ITableColumn } from "@/iti-core-react/dtos/iti-datagrid-column.interface";
import { Template } from "devextreme-react/core/template";
import DxDataGrid, {
	ColumnChooser,
	ColumnChooserSearch,
	ColumnChooserSelection,
	ColumnFixing,
	DataGridRef,
	Editing,
	Export,
	FilterBuilderPopup,
	FilterPanel,
	FilterRow,
	GroupPanel,
	HeaderFilter,
	Pager,
	Paging,
	Position,
	RowDragging,
	Scrolling,
	SearchPanel,
	Selection,
	Sorting,
	StateStoring,
} from "devextreme-react/data-grid";
import {
	CellPreparedEvent,
	ContentReadyEvent,
	ContextMenuPreparingEvent,
	EditingStartEvent,
	EditorPreparedEvent,
	EditorPreparingEvent,
	ExportingEvent,
	FocusedCellChangingEvent,
	InitializedEvent,
	OptionChangedEvent,
	RowClickEvent,
	RowDblClickEvent,
	RowInsertedEvent,
	RowInsertingEvent,
	RowPreparedEvent,
	RowRemovedEvent,
	RowRemovingEvent,
	RowUpdatedEvent,
	RowValidatingEvent,
	SavedEvent,
	SavingEvent,
	SelectionChangedEvent,
	ToolbarPreparingEvent,
} from "devextreme/ui/data_grid_types";
import { t } from "i18next";
import { lazy, useEffect, useMemo, useRef, useState } from "react";
import { ItiMessageService } from "../../services/iti-message-service";
import "./ItiDataGrid.scss";
import { translateMenuItems } from "./ItiDataGridHandles";

const ItiViewSelector = lazy(() => import("./ItiViewSelector/ItiViewSelector"));

type ToolbarButtonName =
	| "addRowButton"
	| "applyFilterButton"
	| "columnChooserButton"
	| "revertButton"
	| "saveButton"
	| "searchPanel"
	| "exportButton"
	| "groupPanel";

export type CheckBoxesModeType = "always" | "none" | "onClick" | "onLongTap";
export type SelectionModeType = "none" | "single" | "multiple";

export interface IToolBarButton {
	icon?: string;
	name?: ToolbarButtonName | string;
	visible?: boolean;
	location?: "after" | "before";
	options?: any;
	onClick?: () => void;
}
interface ItiDataGridProps {
	title?: string;
	keyExpr?: any;
	columns: ITableColumn[];
	width?: any;
	height?: string | number;
	dataSource?: any;
	sortingMode?: "multiple" | "single";
	filterValue?: any;
	remoteOperations?: boolean;
	hoverStateEnabled?: boolean;
	errorRowEnabled?: boolean;
	disabled?: boolean;
	// Opciones de paginacion
	pagingEnabled?: boolean;
	pagerVisible?: boolean;
	pageSize?: number;
	showPageSizeSelector?: boolean;
	allowedPageSizes?: any;
	pagerShowInfo?: boolean;
	pageIndex?: number;
	// Opciones de edicion
	editingMode?: any;
	allowAdding?: boolean;
	allowUpdating?: boolean;
	allowDeleting?: boolean;
	selectTextOnEditStart?: boolean;
	// Opciones de filtrado
	filterRowVisible?: boolean;
	filterPanelVisible?: boolean;
	searchPanelVisible?: boolean;
	// Opciones de persistencia de estado
	stateStoreEnabled?: boolean;
	stateStoringType?: "localStorage" | "sessionStorage" | "custom";
	stateStoringSavingTimeout?: number;
	// Opciones drag and drop - reorder
	allowReordering?: boolean;
	dropFeedbackMode?: "push" | "indicate";
	groupDragging?: string;
	dataDragging?: boolean;
	// Opciones selector de columnas
	columnChooserEnabled?: boolean;
	columnChooserMode?: "dragAndDrop" | "select";
	columnChooserWidth?: number;
	// Seleccion
	selectAllMode?: "allPages" | "page";
	checkBoxesMode?: CheckBoxesModeType;
	selectionMode?: "none" | "single" | "multiple";
	// Opciones selector de vistas
	entity?: string;
	enableViewSelector?: boolean;
	enableStateStoring?: boolean;
	// Opciones toolbar
	toolbarButtons?: IToolBarButton[];
	toolbarVisible?: boolean;
	// Opciones exportacion
	exportName?: string;
	exportEnabled?: boolean;
	// Opciones filter builder
	filterBuilderWidth?: number | string;
	filterBuilderHeight?: number | string;
	// Opciones sobre columnas
	allowColumnReordering?: boolean;
	allowColumnResizing?: boolean;
	columnAutoWidth?: boolean;
	// Opciones de scroll
	scrollingMode?: "standard" | "virtual";
	columnRenderingMode?: "standard" | "virtual";
	scrollShowScrollbar?: "always" | "never" | "onScroll" | "onHover";
	scrollUseNative?: boolean;
	scrollByContent?: boolean;
	scrollByThumb?: boolean;
	// Opciones varias
	titleInToolbar?: boolean;
	titleClass?: string;
	elementAttr?: any;
	tabIndex?: number;
	showBorders?: boolean;
	showColumnLines?: boolean;
	rowAlternationEnabled?: boolean;
	focusedRowEnabled?: boolean;
	groupPanelVisible?: boolean;
	headerFilterVisible?: boolean;
	loadPanel?: any;
	searchPanelEnabled?: boolean;
	clearViewConfigEnabled?: boolean;
	rowButtons?: any;
	fixedRowButtons?: boolean;
	captionRowButtons?: string;
	rowButtonsMinWidth?: number;
	fixedRowPosition?: string;
	visible?: boolean;
	onAddButtonClicked?: () => void;
	onEditingStart?: (e: EditingStartEvent) => void;
	onToolbarPreparing?: (e: ToolbarPreparingEvent) => void;
	onSaved?: (e: SavedEvent) => void;
	onRowValidating?: (e: RowValidatingEvent) => void;
	onCellPrepared?: (e: CellPreparedEvent) => void;
	onContextMenuPreparing?: (e: ContextMenuPreparingEvent) => void;
	onFocusedCellChanging?: (e: FocusedCellChangingEvent) => void;
	onExporting?: (e: ExportingEvent) => void;
	onSelectionChanged?: (e: SelectionChangedEvent) => void;
	onOptionChanged?: (e: OptionChangedEvent) => void;
	onRowInserting?: (e: RowInsertingEvent) => void;
	onRowInserted?: (e: RowInsertedEvent) => void;
	onRowUpdated?: (e: RowUpdatedEvent) => void;
	onRowRemoving?: (e: RowRemovingEvent) => void;
	onRowRemoved?: (e: RowRemovedEvent) => void;
	onRowPrepared?: (e: RowPreparedEvent) => void;
	onRowClick?: (e: RowClickEvent) => void;
	onRowDblClick?: (e: RowDblClickEvent) => void;
	onEditorPrepared?: (e: EditorPreparedEvent) => void;
	onSaving?: (e: SavingEvent) => void;
	onInitializedRef?: (instance: any) => void;
	onEditorPreparing?: (e: EditorPreparingEvent) => void;
	onPageIndexChanged?: (index: any, size: any) => void;
	onPageSizeChanged?: (e: any) => void;
	onInitialized?: (e: InitializedEvent) => void;
	onContentReady?: (e: ContentReadyEvent) => void;
}

export default function ItiDataGrid({
	title,
	keyExpr = "id",
	columns,
	width = "100%",
	height = "auto",
	dataSource,
	sortingMode = "multiple",
	filterValue,
	remoteOperations = false,
	hoverStateEnabled = true,
	errorRowEnabled = false,
	disabled = false,
	// Opciones de paginación
	pagingEnabled = true,
	pagerVisible = true,
	pageSize = 10,
	showPageSizeSelector = false,
	allowedPageSizes = [5, 10, 15, "all"],
	pageIndex,
	editingMode = "row",
	allowAdding = true,
	allowUpdating = true,
	allowDeleting = true,
	selectTextOnEditStart = true,
	// Opciones de filtrado
	filterRowVisible = true,
	filterPanelVisible = false,
	searchPanelVisible = true,
	// Opciones de persistencia de estado
	stateStoreEnabled = false,
	stateStoringType = "localStorage",
	stateStoringSavingTimeout = 500,
	// Opciones drag and drop - reorder
	allowReordering = false,
	dropFeedbackMode = "push",
	groupDragging,
	dataDragging,
	columnChooserEnabled = false,
	columnChooserMode = "select",
	columnChooserWidth = 350,
	// Selección
	selectAllMode = "allPages",
	checkBoxesMode = "always",
	selectionMode = "none",
	// Opciones selector de vistas
	entity,
	enableViewSelector = false,
	// Opciones de toolbar
	toolbarVisible = true,
	toolbarButtons,
	// Opciones exportación
	exportName,
	exportEnabled = false,
	// Opciones filter builder
	filterBuilderWidth,
	filterBuilderHeight,
	// Opciones sobre columnas
	allowColumnReordering = true,
	allowColumnResizing = true,
	columnAutoWidth = false,
	// Opciones de scroll
	scrollingMode = "standard",
	columnRenderingMode = "virtual",
	scrollShowScrollbar = "always",
	scrollUseNative = false,
	scrollByContent = true,
	scrollByThumb = false,
	// Opciones varias
	titleInToolbar = false,
	titleClass = "",
	elementAttr = { class: "basic-grid" },
	tabIndex,
	showBorders = true,
	showColumnLines = true,
	rowAlternationEnabled = false,
	focusedRowEnabled = false,
	groupPanelVisible = false,
	headerFilterVisible = false,
	loadPanel = {
		enabled: !!remoteOperations,
		shading: true,
		shadingColor: "white",
	},
	searchPanelEnabled = false,
	clearViewConfigEnabled = true,
	rowButtons,
	fixedRowButtons = false,
	captionRowButtons = "actions",
	fixedRowPosition = "right",
	rowButtonsMinWidth,
	visible = true,
	onRowClick,
	onRowDblClick,
	onAddButtonClicked,
	onRowInserting,
	onRowInserted,
	onEditingStart,
	onRowUpdated,
	onSaved,
	onRowRemoving,
	onRowRemoved,
	onRowPrepared,
	onRowValidating,
	onCellPrepared,
	onContextMenuPreparing,
	onFocusedCellChanging,
	onExporting,
	onOptionChanged,
	onInitializedRef,
	onSelectionChanged,
	onToolbarPreparing,
	onEditorPreparing,
	onEditorPrepared,
	onSaving,
	onPageIndexChanged,
	onPageSizeChanged,
	onInitialized,
	onContentReady,
}: Readonly<ItiDataGridProps>) {
	const dataGridRef: any = useRef<DataGridRef>(null);
	const isFirtsLoad = useRef(true);

	// Para cuando no haya datos mostrar una tabla vacía solo si está deshabilitado
	const emptyRows = Array(10)
		.fill({})
		.map((_, index) => ({ id: index }));
	const dataSourceGrid = dataSource ? dataSource : disabled ? emptyRows : [];

	const hasData = dataSource?.length > 0;
	const showFilters = hasData || !disabled;
	const storageKey = `storage_${entity}_${localStorage.getItem("userName")}`;
	const storageLastKey = `storage_${entity}_${localStorage.getItem(
		"userName",
	)}_DB_last`;

	const [clearRowValue, setClearRowValue] = useState(0);
	const [dataGridInstance, setDataGridInstance] = useState<any>();
	const [internalPageSize, setInternalPageSize] = useState(pageSize);

	useEffect(() => {
		if (dataGridInstance) {
			dataGridInstance.component.instance().refresh();
			onInitializedGrid(dataGridInstance);
		}
	}, [disabled]);

	const mergedColumns: any = useMemo(() => {
		const hasButtonsColumn = columns.some(
			(column) => column.type === "buttons",
		);

		if (hasButtonsColumn) {
			return columns.map((column) => {
				if (column.type === "buttons") {
					return {
						...column,
						caption: captionRowButtons,
						buttons: column.buttons?.map((button: any) => {
							const matchingRowButton = rowButtons?.find(
								(rb: any) => rb.name === button.name,
							);
							return matchingRowButton || button;
						}),
					};
				}
				return column;
			});
		}

		if (!rowButtons) {
			return [...columns];
		}

		const hasVisibleButton = rowButtons.some(
			(button: any) => button.visible !== false,
		);

		if (!hasVisibleButton) {
			return [...columns];
		}

		return [
			...columns,
			{
				type: "buttons",
				buttons: rowButtons,
				caption: captionRowButtons,
				fixed: fixedRowButtons,
				fixedPosition: fixedRowPosition,
				width: fixedRowButtons ? "100px" : undefined,
				showInColumnChooser: false,
				allowFixing: fixedRowButtons,
				minWidth: rowButtonsMinWidth || 110,
			},
		];
	}, [rowButtons, disabled, allowUpdating, allowDeleting, captionRowButtons]);

	function onResetGrid() {
		const dataGrid = dataGridRef.current;
		if (dataGrid) {
			dataGrid.instance().refresh();
			dataGrid.instance().state(null);
		}
	}

	function onCellPreparedEvent(e: CellPreparedEvent) {
		if (e.rowType === "header" && e.column.type === "buttons") {
			e.column.caption = t("actions");
		}
		if (onCellPrepared) {
			onCellPrepared(e);
		}
	}

	function onContentReadyEvent(e: ContentReadyEvent) {
		prepareView();

		if (onInitializedRef) {
			onInitializedRef(dataGridRef.current);
		}

		const instance = e.component.instance();

		const buttonsColumn: any = instance
			.getVisibleColumns()
			.find((column) => column.type === "buttons");

		if (
			buttonsColumn &&
			buttonsColumn.caption !== (t(captionRowButtons) ?? captionRowButtons)
		) {
			buttonsColumn.caption = t(captionRowButtons) ?? captionRowButtons;

			instance.columnOption(
				buttonsColumn.index,
				"caption",
				buttonsColumn.caption,
			);

			instance.repaintRows([0]);
		}

		if (onContentReady) {
			onContentReady(e);
		}
	}

	function prepareView() {
		const dbLSLast = localStorage.getItem(storageLastKey);
		const lastView = JSON.parse(dbLSLast ? dbLSLast : "[]");

		const dataGrid = dataGridRef.current;

		if (dataGrid && isFirtsLoad.current) {
			isFirtsLoad.current = false;
			if (lastView.length > 0) {
				dataGrid.instance().state(lastView[0].value);
			}
		}
	}

	function onToolbarPreparingEvent(e: ToolbarPreparingEvent) {
		if (toolbarVisible) {
			addDefaultToolbarButtons(e);
			if (toolbarButtons) {
				e.toolbarOptions?.items?.concat(toolbarButtons);
			}
			onToolbarPreparing?.(e);
		}
	}

	function onInitializedGrid(e: any) {
		dataGridRef.current = e.component;

		setDataGridInstance(e);
		onInitialized?.(e);
	}

	function onRowRemovingData(e: any) {
		ItiMessageService.confirmYesNoButtons("messages.question-delete").then(
			(res) => {
				const dataGrid = dataGridRef.current;

				if (res) {
					if (e.key._value) {
						e.key = e.key._value;
					}

					if (editingMode !== "custom") {
						const itemsGrid = dataGrid
							.instance()
							.getDataSource()
							.items()
							.filter((data: any) => data.id !== e.key);

						dataGrid.instance().option("dataSource", itemsGrid);
					}
				}

				e.cancel = !res;
				onRowRemoving?.(e);
			},
		);

		e.cancel = true;
	}

	function addDefaultToolbarButtons(e: any) {
		let searchPanel: any = e.toolbarOptions.items.find(
			(x: any) => x.name === "searchPanel",
		);

		searchPanel = { ...searchPanel, visible: searchPanelEnabled };

		let exportButton: any = e.toolbarOptions.items.find(
			(x: any) => x.name === "exportButton",
		);

		exportButton = { ...exportButton, visible: exportEnabled };

		const addRowButton: any = e.toolbarOptions.items.find(
			(x: any) => x.name === "addRowButton",
		);

		// Selector de vistas
		e.toolbarOptions.items = [
			{
				location: "before",
				visible: enableViewSelector,
				name: "viewSelector",
				template: "viewSelector",
			},
		];

		// Anyadir botón
		const addButton = addRowButtonOnToolbar(addRowButton);
		if (addButton) {
			e.toolbarOptions.items.push(addButton);
		}

		e.toolbarOptions.items = e.toolbarOptions.items.concat([
			// Resetear vista
			{
				location: "after",
				visible: clearViewConfigEnabled,
				name: "clearViewSelection",
				widget: "dxButton",
				options: {
					hint: t("clear-view-selection"),
					icon: "mdi mdi-swap-horizontal",
					onClick: () => clearViewSelection(),
				},
			},
			// Selector de columnas
			{
				location: "after",
				visible: columnChooserEnabled,
				name: "columnChooserButton",
				widget: "dxButton",
				options: {
					hint: t("column-chooser"),
					icon: "columnchooser",
					onClick: () => e.component.showColumnChooser(),
				},
			},
			exportButton,
			searchPanel,
		]);
	}

	function addRowButtonOnToolbar(addButton: any) {
		if (editingMode === "custom") {
			return {
				location: "after",
				visible: allowAdding,
				widget: "dxButton",
				options: {
					hint: t("add"),
					icon: "add",
					onClick: (e: any) => {
						e.cancel = true;
						onAddButtonClicked?.();
					},
				},
			};
		}
		return addButton;
	}

	function onEditingStartGrid(e: any) {
		if (onEditingStart) {
			onEditingStart(e);
		}
		if (editingMode === "custom") {
			e.cancel = true;
		}
	}

	function clearViewSelection() {
		if (storageKey) {
			const dataGrid = dataGridRef.current;
			if (dataGrid) {
				dataGrid.instance().state(null);
				localStorage.setItem(
					storageLastKey,
					JSON.stringify(dataGrid.instance().state()),
				);
				setClearRowValue((value) => value + 1);
			}
		}
	}

	function onSelectionViewChanged(selection: any) {
		const dataGrid = dataGridRef.current;
		if (dataGrid) {
			dataGrid.instance().state(selection);
		}
	}

	function onContextMenuPreparingEvent(e: ContextMenuPreparingEvent) {
		if (e.items) {
			e.items = translateMenuItems(e.items, t);
		}

		if (onContextMenuPreparing) {
			onContextMenuPreparing(e);
		}
	}

	const handlePageSizeChange = (size: number) => {
		if (onPageSizeChanged) {
			onPageSizeChanged(size);
		}
		if (onPageIndexChanged) {
			onPageIndexChanged(0, size);
		}
		setInternalPageSize(size);
	};

	const handlePageIndexChange = (index: number) => {
		if (onPageIndexChanged) {
			onPageIndexChanged(index, pageSize);
		}
	};

	return (
		<div>
			{title && (
				<div
					className={
						titleInToolbar
							? `data-grid-title-toolbar ${titleClass}`
							: `data-grid-title ${titleClass}`
					}
					style={{ backgroundColor: disabled === true ? "#f5f5f5" : "" }}
				>
					{title}
				</div>
			)}
			<DxDataGrid
				keyExpr={keyExpr}
				dataSource={dataSourceGrid}
				columns={mergedColumns}
				width={width}
				height={height}
				remoteOperations={remoteOperations}
				hoverStateEnabled={hoverStateEnabled}
				filterValue={filterValue}
				errorRowEnabled={errorRowEnabled}
				disabled={disabled}
				showBorders={showBorders}
				showColumnLines={showColumnLines}
				tabIndex={tabIndex}
				elementAttr={elementAttr}
				allowColumnReordering={allowColumnReordering}
				allowColumnResizing={allowColumnResizing}
				rowAlternationEnabled={rowAlternationEnabled}
				loadPanel={loadPanel}
				focusedRowEnabled={focusedRowEnabled}
				columnAutoWidth={columnAutoWidth}
				visible={visible}
				onSelectionChanged={onSelectionChanged}
				onInitNewRow={onAddButtonClicked}
				onContentReady={onContentReadyEvent}
				onSaved={onSaved}
				onSaving={onSaving}
				onRowValidating={onRowValidating}
				onCellPrepared={onCellPreparedEvent}
				onContextMenuPreparing={onContextMenuPreparingEvent}
				onFocusedCellChanging={onFocusedCellChanging}
				onExporting={onExporting}
				onInitialized={onInitializedGrid}
				onOptionChanged={onOptionChanged}
				onRowInserting={onRowInserting}
				onRowInserted={onRowInserted}
				onRowUpdated={onRowUpdated}
				onRowRemoved={onRowRemoved}
				onRowRemoving={onRowRemovingData}
				onRowPrepared={onRowPrepared}
				onRowClick={onRowClick}
				onRowDblClick={onRowDblClick}
				onEditingStart={onEditingStartGrid}
				onToolbarPreparing={onToolbarPreparingEvent}
				onEditorPrepared={onEditorPrepared}
				onEditorPreparing={onEditorPreparing}
			>
				{/* Paginación */}
				<Paging
					enabled={pagingEnabled}
					pageSize={internalPageSize}
					pageIndex={pageIndex}
					onPageSizeChange={handlePageSizeChange}
					onPageIndexChange={handlePageIndexChange}
				/>
				<Pager
					showPageSizeSelector={showPageSizeSelector}
					allowedPageSizes={allowedPageSizes}
					showInfo={true}
					visible={pagerVisible}
					showNavigationButtons={true}
				/>

				{/* Selector de columnas */}
				<ColumnChooser
					enabled={columnChooserEnabled}
					mode={columnChooserMode}
					width={columnChooserWidth}
				>
					<Position
						my="right top"
						at="right bottom"
						of=".dx-icon-columnchooser"
					/>

					<ColumnChooserSearch enabled={true} />

					<ColumnChooserSelection allowSelectAll={true} selectByClick={true} />
				</ColumnChooser>

				{/* Exportación del grid */}
				<Export
					enabled={exportEnabled}
					file-name={exportName}
					allowExportSelectedData={true}
				/>

				{/* Buscador */}
				<SearchPanel visible={searchPanelVisible} width={240} />

				{/* Ordenación */}
				<Sorting mode={sortingMode} />

				{/* Scroll */}
				<Scrolling
					columnRenderingMode={columnRenderingMode}
					mode={scrollingMode}
					showScrollbar={scrollShowScrollbar}
					scrollByContent={scrollByContent}
					scrollByThumb={scrollByThumb}
					useNative={scrollUseNative}
				/>

				{/* Arrastrado y soltado */}
				<RowDragging
					allowReordering={allowReordering}
					data={dataDragging}
					group={groupDragging}
					dropFeedbackMode={dropFeedbackMode}
				/>

				{/* Añadir, editar y eliminar en línea */}
				<Editing
					allowUpdating={allowUpdating}
					allowAdding={allowAdding}
					allowDeleting={allowDeleting}
					mode={editingMode}
					selectTextOnEditStart={selectTextOnEditStart}
					confirmDelete={false}
				/>

				{/* Filtro por fila */}
				<FilterRow
					visible={filterRowVisible && showFilters}
					showOperationChooser={true}
				/>

				{/* Panel de filtros */}
				<FilterPanel visible={filterPanelVisible} />

				{/* Almacenaje de configuracion */}
				<StateStoring
					enabled={stateStoreEnabled}
					storageKey={storageKey}
					type={stateStoringType}
					savingTimeout={stateStoringSavingTimeout}
				/>

				{/* Seleccion */}
				<Selection
					selectAllMode={selectAllMode}
					showCheckBoxesMode={checkBoxesMode}
					mode={selectionMode}
				/>

				{/* Agrupacion */}
				<GroupPanel visible={groupPanelVisible} />

				{/* Filtros de la cabecera */}
				<HeaderFilter visible={headerFilterVisible} />

				{/* Constructor de filtros popup */}
				<FilterBuilderPopup
					width={filterBuilderWidth}
					height={filterBuilderHeight}
				/>

				<ColumnFixing enabled={true} />

				{/* Templates */}
				<Template name="viewSelector">
					<div>
						{entity && enableViewSelector && (
							<ItiViewSelector
								storageKey={storageKey}
								onSelectionViewChanged={onSelectionViewChanged}
								clearRowValue={clearRowValue}
								onResetGrid={onResetGrid}
							/>
						)}
					</div>
				</Template>
			</DxDataGrid>
		</div>
	);
}
